/**
 * OAuth 2.0 Authorization Code + PKCE (RFC 7636, S256) primitives for the SPA.
 * Pure functions over Web Crypto; no secrets — the SPA is a public client.
 */

const B64URL = /[+/=]/g;

export function base64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(B64URL, (c) => (c === "+" ? "-" : c === "/" ? "_" : ""));
}

/** A high-entropy URL-safe random string (verifier/state). 32 bytes → 43 chars. */
export function randomUrlSafe(byteLength = 32, cryptoImpl: Crypto = globalThis.crypto): string {
  const bytes = new Uint8Array(byteLength);
  cryptoImpl.getRandomValues(bytes);
  return base64Url(bytes);
}

/** S256 code challenge for a verifier. */
export async function codeChallengeS256(verifier: string, cryptoImpl: Crypto = globalThis.crypto): Promise<string> {
  if (verifier.length < 43 || verifier.length > 128 || /[^A-Za-z0-9._~-]/.test(verifier)) {
    throw new Error("invalid PKCE code verifier");
  }
  const digest = await cryptoImpl.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

export interface OidcConfig {
  readonly authorizeUrl: string;
  readonly tokenUrl: string;
  readonly logoutUrl: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly logoutRedirectUri: string;
  readonly scopes: string;
}

export function authorizeUrl(cfg: OidcConfig, p: { state: string; codeChallenge: string }): string {
  const u = new URL(cfg.authorizeUrl);
  u.searchParams.set("response_type", "code");
  u.searchParams.set("client_id", cfg.clientId);
  u.searchParams.set("redirect_uri", cfg.redirectUri);
  u.searchParams.set("scope", cfg.scopes);
  u.searchParams.set("state", p.state);
  u.searchParams.set("code_challenge", p.codeChallenge);
  u.searchParams.set("code_challenge_method", "S256");
  return u.toString();
}

export function logoutUrl(cfg: OidcConfig): string {
  const u = new URL(cfg.logoutUrl);
  u.searchParams.set("client_id", cfg.clientId);
  u.searchParams.set("logout_uri", cfg.logoutRedirectUri);
  return u.toString();
}

export type CallbackParams =
  | { kind: "code"; code: string; state: string }
  | { kind: "error"; error: string; description: string | null; state: string | null };

/** Parse the redirect back from the identity provider. */
export function parseCallback(search: string): CallbackParams | null {
  const q = new URLSearchParams(search);
  const error = q.get("error");
  if (error) return { kind: "error", error, description: q.get("error_description"), state: q.get("state") };
  const code = q.get("code");
  const state = q.get("state");
  if (code && state) return { kind: "code", code, state };
  return null;
}

/** Decode (NOT verify) JWT claims for display/expiry. The API verifies tokens. */
export function decodeClaims(jwt: string): Record<string, unknown> | null {
  const part = jwt.split(".")[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "="));
    return JSON.parse(decodeURIComponent(escape(json))) as Record<string, unknown>;
  } catch {
    return null;
  }
}
